import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import NumberInDecimals from "../../../../components/inputs/NumberFormatted";
import { formatToPHP } from "../../../../utils/numberFormatter";
import SourceAccountFields, {
  emptySource,
  isSourceComplete,
} from "./SourceAccountFields";

// Amounts are compared in centavos to avoid floating point drift
export const toCents = (value) =>
  Math.round((parseFloat(String(value ?? "").replace(/,/g, "")) || 0) * 100);

// Account balance converted to PHP
export const getBalancePHP = (account) =>
  (Number(account?.amount) || 0) *
  (Number(account?.currency?.currency_rate) || 1);

let lineKey = 0;
export const newLine = (fields = {}) => ({
  key: ++lineKey,
  ...emptySource(),
  amount: "",
  ...fields,
});

// Splits one owner's allocated amount across several sources of funds
function SplitAllocationModal({
  show,
  owner,
  allocatedAmount,
  initialLines,
  subjects,
  accountsBySubject,
  loadAccounts,
  onClose,
  onSave,
  onReset,
  hasCustomSplit,
  isSaving = false,
  // Cheque numbers already saved on this profit distribution
  savedCheques = [],
}) {
  const [lines, setLines] = useState([]);
  const [validated, setValidated] = useState(false);

  // Start from the owner's current split each time the modal opens. Only
  // `show` is watched: the parent passes a new initialLines array on every
  // render, which would wipe the user's edits.
  useEffect(() => {
    if (!show) return;
    setValidated(false);
    setLines(
      initialLines.length > 0
        ? initialLines.map((line) => newLine({ ...line }))
        : [newLine({ amount: String(allocatedAmount) })],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const allocatedCents = toCents(allocatedAmount);
  const splitCents = lines.reduce((sum, line) => sum + toCents(line.amount), 0);
  const remainingCents = allocatedCents - splitCents;

  const updateLine = (key, fields) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...fields } : line)),
    );
  };

  // A new line is prefilled with whatever is still unallocated
  const handleAddLine = () => {
    setLines((prev) => [
      ...prev,
      newLine({
        amount: remainingCents > 0 ? String(remainingCents / 100) : "",
      }),
    ]);
  };

  const handleRemoveLine = (key) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
  };

  // The same account can't be used twice in one owner's split
  const usedAccountIds = (currentKey) =>
    new Set(
      lines
        .filter((line) => line.key !== currentKey && line.account)
        .map((line) => line.account.value),
    );

  const hasIncompleteLine = lines.some(
    (line) => !isSourceComplete(line) || toCents(line.amount) <= 0,
  );
  // A partial split can be saved and finished later; it just can't go over
  const isOverAllocated = remainingCents < 0;
  const canSave = lines.length > 0 && !hasIncompleteLine && !isOverAllocated;

  // Remaining card: green when complete, amber when partial, red when over
  const remainingTone =
    remainingCents === 0 ? "success" : isOverAllocated ? "danger" : "warning";

  const handleSave = () => {
    setValidated(true);
    if (!canSave) return;
    onSave(lines);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>Split Allocation for "{owner?.account_name}"</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Allocated Amount</div>
              <div className="fs-5 fw-bold">{formatToPHP(allocatedAmount)}</div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Split Total</div>
              <div className="fs-5 fw-bold">{formatToPHP(splitCents / 100)}</div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div
              className={`border rounded p-3 h-100 bg-${remainingTone} bg-opacity-10`}
            >
              <div className="text-muted small">Remaining</div>
              <div
                className={`fs-5 fw-bold ${
                  remainingTone === "warning"
                    ? "text-warning-emphasis"
                    : `text-${remainingTone}`
                }`}
              >
                {formatToPHP(remainingCents / 100)}
              </div>
            </div>
          </div>
        </div>

        {lines.length === 0 && (
          <div className="border rounded p-4 text-center text-muted mb-3">
            No split yet. Add a line below.
          </div>
        )}

        {lines.map((line, index) => (
          <div key={line.key} className="border shadow-sm rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold">Line {index + 1}</span>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveLine(line.key)}
                title="Remove line"
              >
                <i className="bx bx-trash"></i>
              </Button>
            </div>

            <SourceAccountFields
              idPrefix={`split-line-${line.key}`}
              value={line}
              onChange={(fields) => updateLine(line.key, fields)}
              subjects={subjects}
              accountsBySubject={accountsBySubject}
              loadAccounts={loadAccounts}
              excludeAccountIds={usedAccountIds(line.key)}
              chequePending={lines
                .filter(
                  (other) =>
                    other.key !== line.key && other.paymentMethod === "Bank",
                )
                .map((other) => other.chequeNumber)}
              chequeIgnore={savedCheques}
              validated={validated}
              sideField={
                <>
                  <Form.Label className="fw-bold">
                    Amount (PHP) <span className="text-danger">*</span>
                  </Form.Label>
                  <NumberInDecimals
                    className={`form-control ${
                      validated && toCents(line.amount) <= 0 ? "is-invalid" : ""
                    }`}
                    style={{ minHeight: "38px" }}
                    value={line.amount}
                    onChange={(value) =>
                      updateLine(line.key, { amount: value })
                    }
                    decimals={2}
                    placeholder="0.00"
                  />
                </>
              }
            />
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={handleAddLine}>
          <i className="bx bx-plus me-1"></i> Add Line
        </Button>

        {validated && !canSave && (
          <Form.Text className="d-block text-danger mt-2">
            {lines.length === 0
              ? "Add at least one line, or use Reset to remove the saved split."
              : hasIncompleteLine
                ? "Every line needs an account and an amount. Bank lines also need a maturity date."
                : `The split can't be more than ${formatToPHP(
                    allocatedAmount,
                  )}.`}
          </Form.Text>
        )}
        {canSave && remainingCents > 0 && (
          <Form.Text className="d-block text-muted mt-2">
            {formatToPHP(remainingCents / 100)} is still unallocated. You can
            save now and allocate the rest later.
          </Form.Text>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {hasCustomSplit && (
            <Button
              variant="link"
              className="text-danger"
              onClick={onReset}
              disabled={isSaving}
            >
              Reset to default source account
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Split"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default SplitAllocationModal;
